// src/services/deliveryService.js
// Class delivery (/api/lesson-deliveries), library suggestions
// (/api/library-suggestions) and staff dashboards (/api/staff-dashboard).

import axiosInstance from "../lib/util/axios";

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

const request = async (label, call) => {
  try {
    const response = await call();
    return response.data;
  } catch (error) {
    console.error(`Error ${label}:`, error);
    throw error;
  }
};

/** Fired after a suggestion is approved or rejected so the sidebar badge refreshes. */
export const SUGGESTIONS_CHANGED_EVENT = "dyen:library-suggestions-changed";

export const suggestionService = {
  /** Returns an array (newest first, max 500). */
  list: (params = {}) =>
    request("fetching library suggestions", () =>
      axiosInstance.get("/api/library-suggestions", { params: cleanParams(params) })
    ),

  /** { suggestion, item, plan } where plan is null unless pending. */
  get: (id) => request(`fetching suggestion ${id}`, () => axiosInstance.get(`/api/library-suggestions/${id}`)),

  approve: async (id, reviewNote = "") => {
    const data = await request(`approving suggestion ${id}`, () =>
      axiosInstance.patch(`/api/library-suggestions/${id}/approve`, { reviewNote })
    );
    window.dispatchEvent(new Event(SUGGESTIONS_CHANGED_EVENT));
    return data;
  },

  reject: async (id, reviewNote) => {
    const data = await request(`rejecting suggestion ${id}`, () =>
      axiosInstance.patch(`/api/library-suggestions/${id}/reject`, { reviewNote })
    );
    window.dispatchEvent(new Event(SUGGESTIONS_CHANGED_EVENT));
    return data;
  },

  pendingCount: async () => {
    const rows = await suggestionService.list({ status: "pending", limit: 500 });
    return Array.isArray(rows) ? rows.length : 0;
  },
};

export const deliveryService = {
  /** Returns an array. Filters: cohortId, programId, taught ("true"|"false"), limit (max 500). */
  list: (params = {}) =>
    request("fetching lesson deliveries", () =>
      axiosInstance.get("/api/lesson-deliveries", { params: cleanParams(params) })
    ),

  get: (id) => request(`fetching lesson delivery ${id}`, () => axiosInstance.get(`/api/lesson-deliveries/${id}`)),

  /**
   * Students-bring reminder. dryRun=true previews recipients and text only.
   * A real send fails with 409 { code: "TEMPLATE_MISSING", message } when the
   * "Class Bring Reminder" template does not exist for the program.
   * @param {string} id
   * @param {{ dryRun?: boolean, classDate?: string, channel?: string }} [options]
   */
  sendReminder: (id, { dryRun = false, classDate, channel = "WhatsApp" } = {}) =>
    request(`sending reminder for delivery ${id}`, () =>
      axiosInstance.post(
        `/api/lesson-deliveries/${id}/reminders`,
        cleanParams({ classDate, channel }),
        { params: dryRun ? { dryRun: "true" } : {} }
      )
    ),
};

export const staffDashboardService = {
  admin: (params = {}) =>
    request("fetching admin staff dashboard", () =>
      axiosInstance.get("/api/staff-dashboard/admin", { params: cleanParams(params) })
    ),
};

/** Best readable message (and code) from an API error. */
export const apiError = (error, fallback = "Something went wrong") => {
  const data = error?.response?.data;
  const message = Array.isArray(data?.errors) && data.errors.length
    ? data.errors.join("; ")
    : data?.message || error?.message || fallback;
  return { message, code: data?.code || "", status: error?.response?.status || 0 };
};
